import * as React from 'react';

import {KeyInfoQueryContextProvider} from '../../../components/MultiRunWorkspace/KeyInfoQueryContext';
import {
  LinePlotPanel,
  PANEL_TYPE,
} from '../../../components/PanelRunsLinePlot/types';
import {ReportSettings} from '../../../components/SettingControls/ReportSettings';
import {
  isPanelGrid,
  PanelGrid,
} from '../../../components/Slate/plugins/panel-grid';
import {useSelector} from '../../../state/hooks';
import * as ReportViewSelectors from '../../../state/reports/selectors';
import * as ReportViewTypes from '../../../state/reports/types';
import {usePart} from '../../../state/views/hooks';
import * as SectionTypes from '../../../state/views/section/types';

interface ReportPanelSettingsProps {
  reportViewRef: ReportViewTypes.ReportViewRef;
}

const ReportPanelSettings = (props: ReportPanelSettingsProps) => {
  const panelSettingsRef = useSelector(
    ReportViewSelectors.getReportPanelSettingsRef(props.reportViewRef)
  );

  const reportRef = useSelector(
    ReportViewSelectors.getReportRef(props.reportViewRef)
  );

  /**
   * Absolute garbage code.
   * I did this because refs in the Slate document are stale on page load
   * because refs change each reload. It is obscenely brittle.
   */
  const useAllLinePlotPanels = () => {
    const {blocks} = usePart(reportRef);
    const panelGrids = blocks.filter(b => isPanelGrid(b)) as PanelGrid[];
    const linePlotIds = new Set<string>();
    panelGrids.forEach(pg => {
      pg.metadata.panelBankSectionConfig.panels.forEach(p => {
        if (p.viewType === PANEL_TYPE) {
          linePlotIds.add(p.__id__);
        }
      });
    });
    const linePlotPanels = useSelector(s => {
      const entries = Object.entries(s.views.parts.panel).filter(([k, v]) =>
        linePlotIds.has(v.__id__)
      );
      const panels = entries.map(([k, v]) => {
        const p = {...v, ref: {...(v as any).ref, id: k}};
        return p;
      });
      return panels;
    });
    return linePlotPanels as unknown as LinePlotPanel[];
  };

  // Aggregate section-derived refs across the report
  const sectionPartsForReport = useSelector(state => {
    const allSections = state.views.parts.section as Record<
      string,
      SectionTypes.SectionNormalized
    >;
    const result: SectionTypes.SectionNormalized[] = [];
    for (const id in allSections) {
      const sec = allSections[id];
      if (sec.panelBankSectionConfigRef?.viewID === props.reportViewRef.id) {
        result.push(sec);
      }
    }
    return result;
  });

  const runSetRefs = React.useMemo(() => {
    const seen = new Set<string>();
    const refs: SectionTypes.SectionNormalized['runSetRefs'] = [];
    for (const sec of sectionPartsForReport) {
      for (const ref of sec.runSetRefs) {
        const key = `${ref.viewID}:${ref.id}`;
        if (!seen.has(key)) {
          seen.add(key);
          refs.push(ref);
        }
      }
    }
    return refs;
  }, [sectionPartsForReport]);

  const customRunNamesRef = React.useMemo(
    () =>
      sectionPartsForReport.find(s => s.customRunNamesRef != null)
        ?.customRunNamesRef,
    [sectionPartsForReport]
  );

  return (
    <KeyInfoQueryContextProvider
      runSetRefs={runSetRefs}
      customRunNamesRef={customRunNamesRef}
      allowMultiRunsetHistoryKeys={true}>
      <ReportSettings
        workspacePanelSettingsRef={panelSettingsRef}
        useAllLinePlotPanels={useAllLinePlotPanels}
      />
    </KeyInfoQueryContextProvider>
  );
};

export default ReportPanelSettings;
